import { useEffect, useRef, useState } from 'react'
import {
  ClassificationRequestError,
  useClassificationExamples,
  useClassificationPreview,
} from '@/api'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'
import { ClassificationResultView } from './result'
import { classificationRequestMessage } from './errors'
import { type ClassificationConfig, type ClassificationTargets } from './config'
import { ActionTimeline } from './action-timeline'
import { Assessments } from './assessments'

export function Preview({
  inboxId,
  config,
  targets,
  dirty,
  reload,
  onReview,
}: {
  inboxId: string
  config: ClassificationConfig
  targets?: ClassificationTargets
  dirty: boolean
  reload: () => Promise<ClassificationConfig | undefined>
  onReview: () => void
}) {
  const examples = useClassificationExamples(inboxId)
  const preview = useClassificationPreview(inboxId)
  const [selected, setSelected] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState('')
  const [refreshError, setRefreshError] = useState('')
  const canPreview = !!config.version && !dirty
  const requested = useRef(false)
  useEffect(() => {
    if (canPreview && !requested.current) {
      requested.current = true
      examples.mutate()
    }
  }, [canPreview, examples])
  const currentResult =
    preview.data?.classification.config_version === config.version
      ? preview.data
      : undefined
  const refresh = async (messageId: string | null) => {
    setRefreshing(true)
    setRefreshError('')
    setNotice('')
    try {
      const latest = await reload()
      if (!latest) throw new Error('Settings unavailable')
      if (!messageId || latest.version !== config.version) {
        preview.reset()
        setNotice(
          'Your saved instructions or actions changed. Review them, then try again.',
        )
        return
      }
      preview.mutate({ message_id: messageId, expected_version: latest.version })
    } catch (error) {
      setRefreshError(classificationRequestMessage(error))
    } finally {
      setRefreshing(false)
    }
  }
  const actionName = (kind: string, id?: string | null) =>
    kind === 'archive'
      ? 'Archive'
      : kind === 'mark_read'
        ? 'Mark as read'
        : kind === 'label'
          ? `Add label “${targets?.labels.find((t) => t.id === id)?.name ?? 'label'}”`
          : kind === 'folder'
            ? `Move${(targets?.folders.find((t) => t.id === id) ?? targets?.labels.find((t) => t.id === id))?.name ? ` to “${(targets?.folders.find((t) => t.id === id) ?? targets?.labels.find((t) => t.id === id))!.name}”` : ''}`
            : kind === 'category'
              ? `Add category “${targets?.categories?.find((t) => t.id === id)?.name ?? 'category'}”`
              : kind === 'keep'
                ? 'Keep where it is'
                : 'Move'
  const result = (
    <div aria-live="polite" className="space-y-3 border-t px-3 pb-3 pt-3">
      {preview.isPending || refreshing ? (
        <p role="status" className="text-sm text-muted-foreground">
          Checking…
        </p>
      ) : currentResult ? (
        <>
          <ActionTimeline
            actions={currentResult.proposed_actions}
            decisions={currentResult.decisions}
            preview
            readinessReason={currentResult.activation.reason}
            nameFor={actionName}
          />
          {currentResult.classification.error && (
            <ClassificationResultView result={currentResult.classification} />
          )}
          <Assessments result={currentResult.classification} />
        </>
      ) : preview.data ? (
        <p role="status" className="text-sm">
          Your instructions or actions changed. Tap the email again.
        </p>
      ) : null}
      {preview.error && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">
            {classificationRequestMessage(preview.error)}
          </p>
          {preview.error instanceof ClassificationRequestError &&
            preview.error.status === 409 && (
              <Button
                variant="outline"
                size="sm"
                disabled={dirty || refreshing}
                onClick={() => refresh(null)}
              >
                Load saved setup
              </Button>
            )}
        </div>
      )}
      {refreshError && (
        <p role="alert" className="text-sm text-destructive">
          {refreshError}
        </p>
      )}
      {notice && (
        <div className="space-y-2">
          <p role="status" className="text-sm">
            {notice}
          </p>
          <Button variant="outline" size="sm" onClick={onReview}>
            Review instructions
          </Button>
        </div>
      )}
    </div>
  )
  return (
    <section aria-label="Preview" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Tap a recent email to see what would happen. Nothing in your inbox changes.
        </p>
        {examples.data && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Load recent emails again"
            disabled={examples.isPending}
            onClick={() => {
              preview.reset()
              setSelected('')
              examples.mutate()
            }}
          >
            <RefreshCw className={`size-4 ${examples.isPending ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>
      {dirty && (
        <p role="status" className="text-sm">
          Save your changes first.
        </p>
      )}
      {!config.version && (
        <p className="text-sm text-muted-foreground">
          Choose this inbox’s actions and save them first.
        </p>
      )}
      {examples.isPending && !examples.data && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading recent emails…
        </p>
      )}
      {examples.error && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">
            {classificationRequestMessage(examples.error)}
          </p>
          <Button variant="outline" size="sm" onClick={() => examples.mutate()}>
            Try again
          </Button>
        </div>
      )}
      {examples.data &&
        (examples.data.messages.length ? (
          <ul className="divide-y rounded-lg border">
            {examples.data.messages.map((message) => {
              const open = selected === message.message_id
              return (
                <li key={message.message_id}>
                  <button
                    type="button"
                    aria-expanded={open}
                    disabled={!canPreview || preview.isPending || refreshing}
                    onClick={() => {
                      preview.reset()
                      setSelected(message.message_id)
                      refresh(message.message_id)
                    }}
                    className={`block w-full px-3 py-3 text-left transition-colors hover:bg-muted/50 disabled:cursor-default ${open ? 'bg-muted/50' : ''}`}
                  >
                    <span className="block break-words text-sm font-medium">
                      {message.subject || '(No subject)'}
                    </span>
                    <span className="block break-all text-xs text-muted-foreground">
                      {message.sender}
                    </span>
                  </button>
                  {open && result}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">
            No recent email yet. Try again after new email arrives.
          </p>
        ))}
    </section>
  )
}
